<template>
    <div class="d-flex align-start justify-space-between">
        <v-tooltip :max-width="300">
            <template v-slot:activator="{ on }">
                <v-btn
                    class="mr-2 mt-1"
                    icon
                    v-on="on"
                    type="button"
                    :disabled="isRefreshing || isLoading"
                    @click="onRefresh">
                    <v-progress-circular
                        v-if="isRefreshing"
                        indeterminate
                        size="20"
                        width="2"
                       >
                    </v-progress-circular>
                    <v-icon v-else>mdi-refresh-circle</v-icon>
                </v-btn>
            </template>
            <span>Refresh the MachShip carrier list for this integration.</span>
        </v-tooltip>

        <v-autocomplete
            v-model="selected"
            :items="carrierOptions"
            :loading="isLoading"
            :disabled="isLoading || isRefreshing"
            item-value="id"
            item-text="name"
            hint="Pick at least one. Required — retry will not fire with an empty list."
            placeholder="Select MachShip carriers to retry"
            persistent-hint
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            hide-no-data
            return-object="false">
            <template v-slot:selection="{ item, parent }">
                <v-chip
                    small
                    close
                    :title="item && item.id ? String(item.id) : ''"
                    @click:close="parent.selectItem(item)">
                    <span v-if="item && item.name">{{ item.name }}</span>
                    <span v-else>Unknown ({{ item }})</span>
                </v-chip>
            </template>

            <template v-slot:item="{ item }">
                <span :title="String(item.id)">{{ item.name }}</span>
            </template>
        </v-autocomplete>
    </div>
</template>

<script>
import { EventBus } from '@/js/event-bus.js'

export default {
    props: {
        integration_id: {
            type: Number,
            required: true,
        },
        value: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            isLoading: false,
            isRefreshing: false,
            carriers: [],
        };
    },

    computed: {
        selected: {
            get() {
                return (this.value || []).map(v => parseInt(v, 10)).filter(v => Number.isFinite(v));
            },
            set(val) {
                const ids = (val || [])
                    .map(v => (typeof v === 'object' && v !== null ? v.id : v))
                    .map(v => parseInt(v, 10))
                    .filter(v => Number.isFinite(v) && v > 0);
                this.$emit('input', ids);
            },
        },
        carrierOptions() {
            // Include any stored ID that isn't in the fetched list so the chip
            // still renders (as "Unknown (id)") instead of disappearing.
            const fetched = this.carriers.map(c => ({ id: parseInt(c.id, 10), name: c.name }));
            const fetchedIds = new Set(fetched.map(c => c.id));
            const stale = (this.value || [])
                .map(v => parseInt(v, 10))
                .filter(v => Number.isFinite(v) && v > 0 && !fetchedIds.has(v))
                .map(id => ({ id, name: `Unknown (${id})` }));
            return [...fetched, ...stale].sort((a, b) => a.name.localeCompare(b.name));
        },
    },

    created() {
        this.fetchCarriers();
    },

    methods: {
        fetchCarriers() {
            this.isLoading = true;
            this.$http
                .get(`/api/machship/carriers?integration_id=${this.integration_id}`)
                .then(({ data }) => {
                    this.carriers = (data.result || []).map(c => ({
                        id: parseInt(c.id, 10),
                        name: c.name,
                    }));
                })
                .catch(err => EventBus.$emit('show-error', err))
                .finally(() => { this.isLoading = false; });
        },

        onRefresh() {
            this.isRefreshing = true;
            this.$http
                .post('/api/machship/refresh', { integration_id: this.integration_id })
                .then(() => {
                    this.fetchCarriers();
                    EventBus.$emit('show-success', 'Carrier list refreshed.');
                })
                .catch(err => EventBus.$emit('show-error', err))
                .finally(() => { this.isRefreshing = false; });
        },
    },
};
</script>
